import {
  MultiSelectCompanies,
  MultiSelectCostCenters,
  MultiSelectCustomFieldValues,
  MultiSelectOrganizationalUnits,
  MultiSelectSites,
  MultiSelectSuppliers,
  MultiSelectUsers,
} from '~/ui/molecules/SelectServerDriven';

import {
  type FilterDraftValue,
  type UniversalBuiltInDrawerFieldConfig,
} from '../../types';

type ServerDrivenInput =
  | 'companies'
  | 'costCenters'
  | 'organizationalUnits'
  | 'sites'
  | 'suppliers'
  | 'users';

type ServerDrivenDrawerFieldConfig = Extract<
  UniversalBuiltInDrawerFieldConfig,
  { input: ServerDrivenInput }
>;

type CustomFieldValuesDrawerFieldConfig = Extract<
  UniversalBuiltInDrawerFieldConfig,
  { input: 'customFieldValues' }
>;

type ServerDrivenOption = {
  readonly id: string;
};

type ServerDrivenSelectProps = {
  readonly className: string;
  readonly filterActive: true;
  readonly isClearable: boolean;
  readonly isMultiSelect: boolean;
  readonly onChange: (
    next: ServerDrivenOption | readonly ServerDrivenOption[],
  ) => void;
  readonly placeholder?: string;
  readonly value: string[];
};

type ServerDrivenDrawerFieldControlProps = {
  readonly fieldConfig:
    | CustomFieldValuesDrawerFieldConfig
    | ServerDrivenDrawerFieldConfig;
  readonly onChange: (next?: FilterDraftValue) => void;
  readonly value: string[];
};

export function ServerDrivenDrawerFieldControl({
  fieldConfig,
  value,
  onChange,
}: ServerDrivenDrawerFieldControlProps) {
  const isMultiSelect = fieldConfig.isMultiSelect ?? true;

  const commonProps: ServerDrivenSelectProps = {
    className: 'min-w-56',
    // Only the cost center, site, and user selects read this; it limits the
    // offered options to active records. Already-selected values resolve by id,
    // so a since-deactivated record in an existing filter still displays.
    filterActive: true,
    isClearable: false,
    isMultiSelect,
    onChange: (next: ServerDrivenOption | readonly ServerDrivenOption[]) => {
      const normalizedNext = 'id' in next ? [next] : next;
      const ids = [];
      for (const entry of normalizedNext) {
        if (entry.id !== '') {
          ids.push(entry.id);
        }
      }

      onChange(ids.length === 0 ? undefined : ids);
    },
    placeholder: fieldConfig.placeholder,
    value,
  };

  if (fieldConfig.input === 'customFieldValues') {
    return (
      <MultiSelectCustomFieldValues
        {...commonProps}
        customFieldId={fieldConfig.customFieldId}
      />
    );
  }

  switch (fieldConfig.input) {
    case 'companies': {
      return <MultiSelectCompanies {...commonProps} />;
    }
    case 'costCenters': {
      return <MultiSelectCostCenters {...commonProps} />;
    }
    case 'organizationalUnits': {
      return <MultiSelectOrganizationalUnits {...commonProps} />;
    }
    case 'sites': {
      return <MultiSelectSites {...commonProps} />;
    }
    case 'suppliers': {
      return <MultiSelectSuppliers {...commonProps} />;
    }
    case 'users': {
      return (
        <MultiSelectUsers
          {...commonProps}
          staticOptions={fieldConfig.staticOptions}
        />
      );
    }
    default: {
      return null;
    }
  }
}
